// frontend/src/ResetPassword.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

export default function ResetPassword({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestError, setRequestError] = useState("");

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get("token") || "";
    setToken(t);
  }, []);

  const valid = useMemo(
    () => !!token && password.length >= 8 && password === confirm,
    [token, password, confirm]
  );

  async function requestReset(e) {
    e.preventDefault();
    setRequestError("");
    setRequestMessage("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setRequestError("Email is required");
      return;
    }

    setRequestBusy(true);
    try {
      await apiFetch("/auth/reset/request", {
        method: "POST",
        body: JSON.stringify({ email: trimmed }),
        skipAuthRedirect: true,
        maxRetries: 0,
      });
      setRequestMessage("If that email is on file, a reset link is on its way.");
      setEmail("");
    } catch (err) {
      setRequestError(err.message || "Failed to request password reset");
    } finally {
      setRequestBusy(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!valid) return;
    setBusy(true);
    try {
      const payload = await apiFetch("/auth/reset", {
        method: "POST",
        body: JSON.stringify({ token, password }),
        skipAuthRedirect: true,
        maxRetries: 0,
      });
      if (payload?.token) {
        localStorage.setItem("authToken", payload.token);
      }
      onSuccess?.(payload?.user || null);
    } catch (e) {
      setError(e.message || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <form className="card stack" onSubmit={requestReset}>
        <h2 className="h2" style={{ margin: 0 }}>Forgot your password?</h2>
        <p className="help">
          Enter the email you use to sign in. If it matches an account, we&apos;ll email you a reset link right away.
        </p>
        <input
          className="input"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        {requestError && (
          <div className="help" style={{ color: "red" }}>
            {requestError}
          </div>
        )}
        {requestMessage && (
          <div className="help" style={{ color: "#16a34a" }}>
            {requestMessage}
          </div>
        )}
        <button className="btn" type="submit" disabled={requestBusy}>
          {requestBusy ? "Sending…" : "Email me a reset link"}
        </button>
      </form>
    );
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <h2 className="h2" style={{ margin: 0 }}>Reset your password</h2>
      <input
        className="input"
        type="password"
        placeholder="New password (min 8 chars)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />
      <input
        className="input"
        type="password"
        placeholder="Confirm new password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoComplete="new-password"
      />

      {password && confirm && password !== confirm && (
        <div className="help" style={{ color: "red" }}>Passwords do not match.</div>
      )}
      {error && <div className="help" style={{ color: "red" }}>{error}</div>}

      <button className="btn" type="submit" disabled={!valid || busy}>
        {busy ? "Resetting…" : "Reset password & sign in"}
      </button>
    </form>
  );
}
